/**
 * ThemeSelector Component
 *
 * Allows users to select their preferred theme (light, dark, or system)
 */

import React, { useEffect } from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface ThemeSelectorProps {
  value: 'light' | 'dark' | 'system';
  onChange: (theme: 'light' | 'dark' | 'system') => void;
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({ value, onChange }) => {
  // Applies the theme class to <html>. This app is client-rendered only (no
  // SSR/hydration -- see src/main.tsx's plain createRoot().render()), so
  // there's no server/client markup mismatch to guard against and no need
  // for a "mounted" gate before touching the DOM here.
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');

    if (value === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(value);
    }
  }, [value]);

  const themes = [
    {
      id: 'light' as const,
      label: 'Light',
      icon: Sun,
      description: 'Light theme',
    },
    {
      id: 'dark' as const,
      label: 'Dark',
      icon: Moon,
      description: 'Dark theme',
    },
    {
      id: 'system' as const,
      label: 'System',
      icon: Monitor,
      description: 'Use system theme',
    },
  ];

  return (
    <div className="space-y-3">
      <Label>Theme</Label>
      <div className="grid grid-cols-3 gap-3">
        {themes.map((theme) => {
          const Icon = theme.icon;
          const isActive = value === theme.id;

          return (
            <Button
              key={theme.id}
              variant="outline"
              className={cn(
                'h-auto flex-col gap-2 p-4',
                isActive && 'border-primary bg-primary/5'
              )}
              onClick={() => onChange(theme.id)}
            >
              <Icon className={cn('h-5 w-5', isActive && 'text-primary')} />
              <div className="space-y-1 text-center">
                <div className={cn('text-sm font-medium', isActive && 'text-primary')}>
                  {theme.label}
                </div>
                <div className="text-xs text-muted-foreground">
                  {theme.description}
                </div>
              </div>
            </Button>
          );
        })}
      </div>
    </div>
  );
};
